import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';
import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const FunctionalityCMS = () => {
    const { code } = useParams();
    const formRef = useRef();

    const [formData, setFormData] = useState({
        section_one_heading: "",
        section_one_sub_heading: "",
        section_two_heading: "",
        section_two_sub_heading: "",
        section_two_paragraph: "",
        section_three_heading: "",
        section_three_sub_heading: "",
        section_three_paragraph: "",
        section_four_heading: "",
        section_four_sub_heading: "",
        section_four_paragraph: "",
        section_five_heading: "",
        section_five_sub_heading: "",
        section_five_paragraph: "",
        section_six_heading: "",
        section_six_sub_heading: "",
        section_six_paragraph: "",
        section_seven_heading: "",
        section_seven_sub_heading: "",
        section_seven_cta_text: "",
        section_seven_cta_link: ""
    });

    const [images, setImages] = useState({
        section_one_background_img: null,
        section_two_img: null,
        section_four_img: null,
        section_five_background_img: null,
        section_six_img: null,
        section_seven_background_img: null,
        section_seven_background_video: null
    });

    const [imagePreviews, setImagePreviews] = useState({
        section_one_background_img: null,
        section_two_img: null,
        section_four_img: null,
        section_five_background_img: null,
        section_six_img: null,
        section_seven_background_img: null,
        section_seven_background_video: null
    });

    const [sectionThreeRepeater, setSectionThreeRepeater] = useState([]);

    const [submitting, setSubmitting] = useState(false);

    const [rawMode, setRawMode] = useState({});
    const toggleRawMode = (field) =>
        setRawMode(prev => ({ ...prev, [field]: !prev[field] }));

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [field]: file }));
            setImagePreviews(prev => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    const handleRepeaterChange = (setter) => (index, field, value) => {
        setter(prev => {
            const newArr = [...prev];
            newArr[index][field] = value;
            return newArr;
        });
    };

    const handleAddRepeater = (setter, emptyObj) => () => {
        setter(prev => [...prev, emptyObj]);
    };

    const handleRemoveRepeater = (setter) => (index) => {
        setter(prev => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/${code}`);
                if (res.status === 200) {
                    const data = res.data.cms;
                    setFormData({
                        section_one_heading: data.section_one_heading || "",
                        section_one_sub_heading: data.section_one_sub_heading || "",
                        section_two_heading: data.section_two_heading || "",
                        section_two_sub_heading: data.section_two_sub_heading || "",
                        section_two_paragraph: data.section_two_paragraph || "",
                        section_three_heading: data.section_three_heading || "",
                        section_three_sub_heading: data.section_three_sub_heading || "",
                        section_three_paragraph: data.section_three_paragraph || "",
                        section_four_heading: data.section_four_heading || "",
                        section_four_sub_heading: data.section_four_sub_heading || "",
                        section_four_paragraph: data.section_four_paragraph || "",
                        section_five_heading: data.section_five_heading || "",
                        section_five_sub_heading: data.section_five_sub_heading || "",
                        section_five_paragraph: data.section_five_paragraph || "",
                        section_six_heading: data.section_six_heading || "",
                        section_six_sub_heading: data.section_six_sub_heading || "",
                        section_six_paragraph: data.section_six_paragraph || "",
                        section_seven_heading: data.section_seven_heading || "",
                        section_seven_sub_heading: data.section_seven_sub_heading || "",
                        section_seven_cta_text: data.section_seven_cta_text || "",
                        section_seven_cta_link: data.section_seven_cta_link || "",
                    });

                    setImagePreviews({
                        section_one_background_img: data.section_one_background_img ? `${res.data.base_url}${data.section_one_background_img}` : null,
                        section_two_img: data.section_two_img ? `${res.data.base_url}${data.section_two_img}` : null,
                        section_four_img: data.section_four_img ? `${res.data.base_url}${data.section_four_img}` : null,
                        section_five_background_img: data.section_five_background_img ? `${res.data.base_url}${data.section_five_background_img}` : null,
                        section_six_img: data.section_six_img ? `${res.data.base_url}${data.section_six_img}` : null,
                        section_seven_background_img: data.section_seven_background_img ? `${res.data.base_url}${data.section_seven_background_img}` : null,
                        section_seven_background_video: data.section_seven_background_video ? `${res.data.base_url}${data.section_seven_background_video}` : null,
                    });

                    setSectionThreeRepeater(data.section_three_repeater || []);
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        $.validator.addMethod('ckeditorRequired', function (value) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, 'This field is required');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    section_one_heading: { required: true },
                    section_two_paragraph: { ckeditorRequired: true },
                },
                ignore: ".ck-editor *",
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));

            Object.entries(images).forEach(([key, file]) => {
                if (file) data.append(key, file);
            });

            data.append("section_three_repeater", JSON.stringify(sectionThreeRepeater));

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/${code}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("success", res.data.message, "Success");
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/functionality`}>Functionality Page</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Functionality Page</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    {/* Section 1: Hero */}
                                    <div className="col-12"><h5 className="mb-3">Section One: Hero</h5></div>
                                    {[
                                        { name: 'section_one_heading', label: 'Heading' },
                                        { name: 'section_one_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_one_background_img')} />
                                        {imagePreviews.section_one_background_img && (
                                            <img src={imagePreviews.section_one_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 2: Real-time Inventory */}
                                    <div className="col-12"><h5 className="mb-3">Section Two: Real-time Inventory</h5></div>
                                    {[
                                        { name: 'section_two_heading', label: 'Heading' },
                                        { name: 'section_two_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_two_paragraph')}
                                            >
                                                {rawMode['section_two_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_two_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_two_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_two_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={{
                                                    toolbar: [
                                                        'heading', '|',
                                                        'bold', 'italic', 'link', 'code', 'codeBlock',
                                                        '|', 'bulletedList', 'numberedList'
                                                    ]
                                                }}
                                                data={formData.section_two_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_two_paragraph: data }));
                                                    const hiddenInput = document.querySelector('input[name="section_two_paragraph"]');
                                                    if (hiddenInput) hiddenInput.value = data;
                                                }}
                                            />
                                        )}
                                        <input
                                            type="hidden"
                                            name="section_two_paragraph"
                                            value={formData.section_two_paragraph}
                                        />
                                    </div>

                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_two_img')} />
                                        {imagePreviews.section_two_img && (
                                            <img src={imagePreviews.section_two_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 3: Why Our Platform */}
                                    <div className="col-12"><h5 className="mb-3">Section Three: Why Our Platform</h5></div>
                                    {[
                                        { name: 'section_three_heading', label: 'Heading' },
                                        { name: 'section_three_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}

                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_three_paragraph')}
                                            >
                                                {rawMode['section_three_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_three_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_three_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_three_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={formData.section_three_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_three_paragraph: data }));
                                                }}
                                                config={{
                                                    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'mediaEmbed', 'undo', 'redo'],
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-bold">Benefits Repeater</label>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Icon</th>
                                                        <th>Title</th>
                                                        <th width="10%">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sectionThreeRepeater.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <input type="text" className="form-control form-control-sm" value={item.icon || ''} onChange={(e) => handleRepeaterChange(setSectionThreeRepeater)(index, 'icon', e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <input type="text" className="form-control form-control-sm" value={item.title || ''} onChange={(e) => handleRepeaterChange(setSectionThreeRepeater)(index, 'title', e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <button type="button" className="btn btn-sm btn-danger px-3 py-1" onClick={() => handleRemoveRepeater(setSectionThreeRepeater)(index)}><i className="fa fa-trash"></i></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <button type="button" className="btn btn-sm btn-light" onClick={handleAddRepeater(setSectionThreeRepeater, { icon: '', title: '' })}><i className="fa fa-plus"></i> Add Benefit</button>
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 4: Smart Sourcing */}
                                    <div className="col-12"><h5 className="mb-3">Section Four: Smart Sourcing</h5></div>
                                    {[
                                        { name: 'section_four_heading', label: 'Heading' },
                                        { name: 'section_four_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}

                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_four_paragraph')}
                                            >
                                                {rawMode['section_four_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_four_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_four_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_four_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={formData.section_four_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_four_paragraph: data }));
                                                }}
                                                config={{
                                                    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'mediaEmbed', 'undo', 'redo'],
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_four_img')} />
                                        {imagePreviews.section_four_img && (
                                            <img src={imagePreviews.section_four_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 5: Seamless Transactions */}
                                    <div className="col-12"><h5 className="mb-3">Section Five: Seamless Transactions</h5></div>
                                    {[
                                        { name: 'section_five_heading', label: 'Heading' },
                                        { name: 'section_five_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}

                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_five_paragraph')}
                                            >
                                                {rawMode['section_five_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_five_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_five_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_five_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={formData.section_five_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_five_paragraph: data }));
                                                }}
                                                config={{
                                                    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'mediaEmbed', 'undo', 'redo'],
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_five_background_img')} />
                                        {imagePreviews.section_five_background_img && (
                                            <img src={imagePreviews.section_five_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 6: Logistics */}
                                    <div className="col-12"><h5 className="mb-3">Section Six: Logistics</h5></div>
                                    {[
                                        { name: 'section_six_heading', label: 'Heading' },
                                        { name: 'section_six_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}

                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_six_paragraph')}
                                            >
                                                {rawMode['section_six_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_six_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_six_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_six_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={formData.section_six_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_six_paragraph: data }));
                                                }}
                                                config={{
                                                    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'mediaEmbed', 'undo', 'redo'],
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_six_img')} />
                                        {imagePreviews.section_six_img && (
                                            <img src={imagePreviews.section_six_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 7: CTA */}
                                    <div className="col-12"><h5 className="mb-3">Section Seven: CTA</h5></div>
                                    {[
                                        { name: 'section_seven_heading', label: 'Heading' },
                                        { name: 'section_seven_sub_heading', label: 'Sub Heading' },
                                        { name: 'section_seven_cta_text', label: 'CTA Text' },
                                        { name: 'section_seven_cta_link', label: 'CTA Link' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-3 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_seven_background_img')} />
                                        {imagePreviews.section_seven_background_img && (
                                            <img src={imagePreviews.section_seven_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Video <small className="text-muted">(.mp4)</small></label>
                                        <input type="file" className="form-control" accept="video/mp4" onChange={(e) => handleImageChange(e, 'section_seven_background_video')} />
                                        {imagePreviews.section_seven_background_video && (
                                            <video src={imagePreviews.section_seven_background_video} width={300} className="mt-2 rounded" controls />
                                        )}
                                    </div>
                                </div>
                                <div className="pt-3">
                                    <button type="button" className="btn btn-sm btn-primary me-2" onClick={handleFormSubmit} disabled={submitting}>
                                        {submitting ? <><i className="spinner-border spinner-border-sm me-1"></i>Saving...</> : <><i className="fa fa-floppy-disk me-2"></i>Save</>}
                                    </button>
                                    <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FunctionalityCMS;
